// FlightInformation.js
import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { FaPlaneDeparture, FaPlaneArrival, FaClock, FaDollarSign, FaCalendarAlt } from 'react-icons/fa';

const FlightInfoPage = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const [selectedDates, setSelectedDates] = useState({
    outbound: '',
    return: ''
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const handleDateChange = (direction, date) => {
    setSelectedDates(prev => ({
      ...prev,
      [direction]: date
    }));
  };

  const handleBookingRedirect = () => {
    window.location.href = "https://www.ethiopianairlines.com/en-et/flights-to-semera";
  };

  const FlightCard = ({ direction, details }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={controls}
      variants={{
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay: direction === 'outbound' ? 0.2 : 0.4 }}
    >
      <StyledCard>
        <div className="card-header">
          <h3>{details.title}</h3>
          <div className="flight-route">
            {direction === 'outbound' ? <FaPlaneDeparture /> : <FaPlaneArrival />}
          </div>
        </div>
        
        <div className="flight-details">
          <FlightDetail>
            <div className="detail-label">
              <FaPlaneDeparture />
              <span>Departure</span>
            </div>
            <span>{details.departure}</span>
          </FlightDetail>

          <FlightDetail>
            <div className="detail-label">
              <FaPlaneArrival />
              <span>Arrival</span>
            </div>
            <span>{details.arrival}</span>
          </FlightDetail>

          <FlightDetail>
            <div className="detail-label">
              <FaClock />
              <span>Duration</span>
            </div>
            <span>{details.duration}</span>
          </FlightDetail>

          <FlightDetail>
            <div className="detail-label">
              <FaDollarSign />
              <span>Price</span>
            </div>
            <span>{details.price}</span>
          </FlightDetail>

          <div className="date-selector">
            <div className="detail-label">
              <FaCalendarAlt />
              <span>Select Date</span>
            </div>
            <Form.Control
              type="date"
              value={selectedDates[direction]}
              onChange={(e) => handleDateChange(direction, e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <motion.button
            className="book-button"
            onClick={handleBookingRedirect}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Book Now
          </motion.button>
        </div>
      </StyledCard>
    </motion.div>
  );

  return (
    <StyledFlightPage ref={ref}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          variants={{
            visible: { opacity: 1, y: 0 }
          }}
          transition={{ duration: 0.6 }}
          className="page-header"
        >
          <h1>Flight Information</h1>
          <p>Addis Abeba ↔ Semera</p>
          <div className="header-accent"></div>
        </motion.div>

        <Row className="flight-content">
          <Col lg={6}>
            <FlightCard
              direction="outbound"
              details={{
                title: "Addis Abeba to Semera",
                departure: "08:00 AM",
                arrival: "09:30 AM",
                duration: "1 hr 30 min",
                price: "$120"
              }}
            />
          </Col>

          <Col lg={6}>
            <FlightCard
              direction="return"
              details={{
                title: "Semera to Addis Abeba",
                departure: "05:00 PM",
                arrival: "06:30 PM",
                duration: "1 hr 30 min",
                price: "$120"
              }}
            />
          </Col>
        </Row>
      </Container>
    </StyledFlightPage>
  );
};

const StyledFlightPage = styled.div`
  padding: 120px 0;
  background: linear-gradient(135deg, #6dd5fa 0%, #ffffff 100%);
  min-height: 100vh;

  .page-header {
    text-align: center;
    margin-bottom: 60px;
    color: #1e293b;

    h1 {
      font-size: 3.5rem;
      font-weight: 700;
      margin-bottom: 10px;
    }

    p {
      font-size: 1.2rem;
      opacity: 0.8;
      margin-bottom: 20px;
    }

    .header-accent {
      width: 80px;
      height: 4px;
      background: linear-gradient(45deg, #2d7ba8, #34495e);
      margin: 0 auto;
      border-radius: 2px;
    }
  }
`;

const StyledCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 30px;

  .card-header {
    background: linear-gradient(135deg, #2d7ba8, #34495e);
    color: white;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 1.5rem;
    }

    .flight-route {
      font-size: 1.5rem;
    }
  }
.book-button{
background: linear-gradient(135deg, #2d7ba8, #34495e);
  color: white;
  padding: 15px 20px;
  padding-buttom: 15px;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-width: 200px;
  letter-spacing: 1px;
}
  .flight-details {
    padding: 30px;
  }
`;

const FlightDetail = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e2e8f0;

  .detail-label {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #64748b;

    svg {
      font-size: 1.2rem;
    }
  }

  &:last-child {
    border-bottom: none;
  }
`;

export default FlightInfoPage;